import { AdminIntro, AdminNav, AdminMetrics, AdminSearch, normalizeSearch } from "../../components/AdminWorkspace";
import { Add, AccountTreeOutlined, ForumOutlined, DeleteOutline, Edit } from "@material-ui/icons";
import { Link as RouterLink } from "react-router-dom";
import React, { useEffect, useReducer, useState, useContext } from "react";

import {
  Button,
  IconButton,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
} from "@material-ui/core";

import MainContainer from "../../components/MainContainer";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import EmptyState from "../../components/EmptyState";
import { usePanelStyles, TableCard, RowActions } from "../../components/FormFields/panel";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import QueueModal from "../../components/QueueModal";
import { toast } from "react-toastify";
import ConfirmationModal from "../../components/ConfirmationModal";
// import { SocketContext } from "../../context/Socket/SocketContext";
import { AuthContext } from "../../context/Auth/AuthContext";
import ForbiddenPage from "../../components/ForbiddenPage";
import { getTokens } from "../../styles/tokens";

const useStyles = makeStyles((theme) => {
  const t = getTokens(theme.palette.type, theme.palette.primary.main);
  return {
    nameCell: { display: "flex", alignItems: "center", gap: 10, minWidth: 0 },
    nameText: { minWidth: 0, display: "flex", flexDirection: "column" },
    name: { fontWeight: 600, color: t.text, overflowWrap: "anywhere" },
    // no celular as colunas ID/Ordem/Saudação somem e viram esta linha sob o nome
    nameMeta: {
      display: "none",
      marginTop: 2,
      fontSize: 12,
      color: t.textSecondary,
      [theme.breakpoints.down("xs")]: { display: "block" },
    },
    hideXs: { [theme.breakpoints.down("xs")]: { display: "none" } },
    numCell: { width: 90, whiteSpace: "nowrap", color: t.textSecondary, fontVariantNumeric: "tabular-nums" },
    greeting: {
      maxWidth: 420,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      color: t.textSecondary,
    },
    greetingEmpty: { color: t.textMuted },
    actionsCell: { width: 96 },
  };
});

const reducer = (state, action) => {
  if (action.type === "LOAD_QUEUES") {
    const queues = action.payload;
    const newQueues = [];

    queues.forEach((queue) => {
      const queueIndex = state.findIndex((q) => q.id === queue.id);
      if (queueIndex !== -1) {
        state[queueIndex] = queue;
      } else {
        newQueues.push(queue);
      }
    });

    return [...state, ...newQueues];
  }

  if (action.type === "UPDATE_QUEUES") {
    const queue = action.payload;
    const queueIndex = state.findIndex((u) => u.id === queue.id);

    if (queueIndex !== -1) {
      state[queueIndex] = queue;
      return [...state];
    } else {
      return [queue, ...state];
    }
  }

  if (action.type === "DELETE_QUEUE") {
    const queueId = action.payload;
    const queueIndex = state.findIndex((q) => q.id === queueId);
    if (queueIndex !== -1) {
      state.splice(queueIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const Queues = () => {
  const classes = useStyles();
  const p = usePanelStyles();

  const [queues, dispatch] = useReducer(reducer, []);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const visibleQueues = queues.filter(queue => normalizeSearch(`${queue.name} ${queue.greetingMessage || ""}`).includes(normalizeSearch(search)));

  const [queueModalOpen, setQueueModalOpen] = useState(false);
  const [selectedQueue, setSelectedQueue] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  //   const socketManager = useContext(SocketContext);
  const { user, socket } = useContext(AuthContext);
  const companyId = user.companyId;


  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/queue");
        dispatch({ type: "LOAD_QUEUES", payload: data });

        setLoading(false);
      } catch (err) {
        toastError(err);
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {

    const onQueueEvent = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_QUEUES", payload: data.queue });
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_QUEUE", payload: data.queueId });
      }
    };
    socket.on(`company-${companyId}-queue`, onQueueEvent);

    return () => {
      socket.off(`company-${companyId}-queue`, onQueueEvent);
    };
  }, [socket, companyId]);

  const handleOpenQueueModal = () => {
    setQueueModalOpen(true);
    setSelectedQueue(null);
  };

  const handleCloseQueueModal = () => {
    setQueueModalOpen(false);
    setSelectedQueue(null);
  };

  const handleEditQueue = (queue) => {
    setSelectedQueue(queue);
    setQueueModalOpen(true);
  };

  const handleCloseConfirmationModal = () => {
    setConfirmModalOpen(false);
    setSelectedQueue(null);
  };

  const handleDeleteQueue = async (queueId) => {
    try {
      await api.delete(`/queue/${queueId}`);
      toast.success(i18n.t("Queue deleted successfully!"));
    } catch (err) {
      toastError(err);
    }
    setSelectedQueue(null);
  };

  return (
    <MainContainer maxWidth scroll>
      <ConfirmationModal
        title={
          selectedQueue &&
          `${i18n.t("queues.confirmationModal.deleteTitle")} ${selectedQueue.name
          }?`
        }
        open={confirmModalOpen}
        onClose={handleCloseConfirmationModal}
        onConfirm={() => handleDeleteQueue(selectedQueue.id)}
      >
        {i18n.t("queues.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <QueueModal
        open={queueModalOpen}
        onClose={handleCloseQueueModal}
        queueId={selectedQueue?.id}
        onEdit={(res) => {
          if (res) {
            setTimeout(() => {
              handleEditQueue(res)
            }, 500)
          }
        }}
      />
      {user.profile === "user" ?
        <ForbiddenPage />
        :
        <>
          <AdminIntro title="Filas de atendimento" description="Organize os setores de atendimento, as mensagens de boas-vindas e as regras de encaminhamento." actions={<>{user.showFlow === "enabled" && <Button component={RouterLink} to="/flowbuilders" variant="outlined" startIcon={<AccountTreeOutlined />}>Fluxos de conversa</Button>}<Button variant="contained" color="primary" onClick={handleOpenQueueModal} startIcon={<Add />}>Nova fila</Button></>} />
          <AdminNav />
          <AdminMetrics items={[{ value: queues.length, label: "Filas cadastradas" }, { value: queues.filter(queue => queue.greetingMessage).length, label: "Com mensagem de boas-vindas" }]} />
          <AdminSearch value={search} onChange={setSearch} placeholder="Buscar fila ou mensagem…" />
          <TableCard>
            {!loading && !visibleQueues.length ? (
              <EmptyState
                icon={<ForumOutlined />}
                title={search ? "Nenhuma fila corresponde à busca." : "Nenhuma fila cadastrada"}
                message={search ? undefined : "Crie uma fila para organizar os setores e configurar o chatbot."}
                action={!search && (
                  <Button variant="contained" color="primary" onClick={handleOpenQueueModal} startIcon={<Add />}>
                    Nova fila
                  </Button>
                )}
              />
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Fila</TableCell>
                    <TableCell className={classes.hideXs}>{i18n.t("queues.table.ID")}</TableCell>
                    <TableCell className={classes.hideXs}>Ordem</TableCell>
                    <TableCell className={classes.hideXs}>{i18n.t("queues.table.greeting")}</TableCell>
                    <TableCell align="right">{i18n.t("queues.table.actions")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleQueues.map((queue) => (
                    <TableRow key={queue.id} hover>
                      <TableCell>
                        <div className={classes.nameCell}>
                          <span className={p.colorDot} style={{ backgroundColor: queue.color || "#64748b" }} aria-hidden="true" />
                          <div className={classes.nameText}>
                            <span className={classes.name}>{queue.name}</span>
                            <span className={classes.nameMeta}>#{queue.id} · Ordem {queue.orderQueue ?? "—"}</span>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className={`${classes.numCell} ${classes.hideXs}`}>{queue.id}</TableCell>
                      <TableCell className={`${classes.numCell} ${classes.hideXs}`}>{queue.orderQueue ?? "—"}</TableCell>
                      <TableCell className={classes.hideXs}>
                        <div
                          className={`${classes.greeting} ${queue.greetingMessage ? "" : classes.greetingEmpty}`}
                          title={queue.greetingMessage || undefined}
                        >
                          {queue.greetingMessage || "Sem mensagem de boas-vindas"}
                        </div>
                      </TableCell>
                      <TableCell align="right" className={classes.actionsCell}>
                        <RowActions>
                          <Tooltip title="Configurar fila">
                            <IconButton aria-label={`Editar fila ${queue.name}`} onClick={() => handleEditQueue(queue)}>
                              <Edit />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Excluir fila">
                            <IconButton aria-label={`Excluir fila ${queue.name}`} onClick={() => { setSelectedQueue(queue); setConfirmModalOpen(true); }}>
                              <DeleteOutline />
                            </IconButton>
                          </Tooltip>
                        </RowActions>
                      </TableCell>
                    </TableRow>
                  ))}
                  {loading && <TableRowSkeleton columns={5} />}
                </TableBody>
              </Table>
            )}
          </TableCard>
        </>}
    </MainContainer>
  );
};

export default Queues;
